import { toast } from "react-toastify";
import fetcher from "../../lib/fetcher";
import useSWRMutation from "swr/mutation";
import { EMarketTypes } from "../../interfaces";
import { IFillDataWizardBuy } from "../../components/pages/buy/components/wizard";
import { IFillDataWizardSell } from "../../components/pages/sell/components/wizard";
import { IFillDataWizardDealService } from "../../components/pages/dealService/components/wizard";
import {
  EDealRequestCommunicationType,
  EDealRequestTimeInterval,
  EDealRequestViewType,
  EPurchasePurpose,
  EReadinessForLowerSellPrice,
  ESideDeal,
  ESpecificsOfSale,
} from "../../components/shared/wizard/steps/types";
import { getSpecificsOfSale } from "../../lib/utils";

export enum EDealRequestType {
  Purchase = "Purchase",
  Sale = "Sale",
  DealService = "DealService",
  Other = "Other",
  Ransom = "Ransom",
  Estimate = "Estimate",
}

export type TCommonWizardsSchema = Partial<
  IFillDataWizardBuy &
    IFillDataWizardSell &
    IFillDataWizardDealService & { facilityId: number }
>;
export interface IDealPurchaseRequest {
  marketType: EMarketTypes;
  type: EDealRequestType.Purchase;
  communicationType: EDealRequestCommunicationType;
  communicationInterval: EDealRequestTimeInterval;
  purchase: {
    readyForCredit?: boolean;
    purpose?: EPurchasePurpose;
    viewTime: EDealRequestTimeInterval;
    viewType: EDealRequestViewType;
    desiredLocationIds?: number[];
    rejectedLocationIds?: number[];
    facilityTypeIds?: number[];
    desiredApartmentComment?: string;
    facilityId?: number;
  };
}

export interface IDealPurchaseRequestByFacilityId {
  marketType: EMarketTypes;
  type: EDealRequestType.Purchase;
  purchase: {
    facilityId?: number;
  };
}

export interface IDealSaleRequest {
  marketType: EMarketTypes;
  type: EDealRequestType.Sale;
  communicationType: EDealRequestCommunicationType;
  communicationInterval: EDealRequestTimeInterval;
  sale: {
    externalFacility: {
      streetId?: number;
      complexId?: number;
      areaInSqM?: number;
      buildingId?: number;
      typeId?: number;
    };
    apartmentNumber?: string;
    desiredPrice?: number;
    demonstrationType?: EDealRequestViewType;
    demonstrationTime?: EDealRequestTimeInterval;
    readinessForLowerSellPrice?: EReadinessForLowerSellPrice;
    specificsOfSale?: ESpecificsOfSale[];
    estimatedPrice?: number;
  };
}
export interface IDealRansomRequest {
  marketType: EMarketTypes;
  type: EDealRequestType.Ransom;
  ransom: {
    externalFacility: {
      streetId?: number;
      complexId?: number;
      areaInSqM: number;
      buildingId: number;
      typeId?: number;
      specificsOfSale?: ESpecificsOfSale[];
    };
    estimatedPrice?: number;
    apartmentNumber: string;
  };
}
export interface IDealEstimateRequest {
  marketType: EMarketTypes;
  type: EDealRequestType.Estimate;
  estimate: {
    externalFacility: {
      streetId?: number;
      complexId?: number;
      areaInSqM: number;
      buildingId: number;
      typeId?: number;
    };
  };
}
export interface IDealServiceRequest {
  marketType: EMarketTypes;
  type: EDealRequestType.DealService;
  dealService: {
    externalFacility: {
      streetId?: number;
      complexId?: number;
      buildingId?: number;
      areaInSqM?: number;
      typeId?: number;
    };
    participationDeal: ESideDeal;
    isAllParticipationDealInCity: boolean;
    specificsOfDeal?: ESpecificsOfSale[];
  };
}

const ERROR_TEXT = "Что-то не так перепроверьте заполненые поля";

const useDealRequest = () => {
  async function dealRequest(
    url: string,
    {
      arg,
    }: {
      arg: {
        type: EDealRequestType;
        data: TCommonWizardsSchema;
        marketType: EMarketTypes;
      };
    }
  ) {
    try {
      let request: unknown | undefined = undefined;

      const {
        communicationType,
        timeIntervalTypeCommunication,
        timeIntervalTypeView,
        viewRealtorParticipation,
        finance,
        aim,
        desiredLocationIds,
        rejectedLocationIds,
        selectedFacilityTypeIds,
        freeComment,
        buildingId,
        areaInSqM,
        apartmentNumber,
        streetId,
        complexId,
        facilityTypeId,
        wishPriceSell,
        offersForClient,
        sideDeal,
        isAllParticipantDealInCity,
        facilityId,
        selectedFacilityTypeOwnFacility,
        estimatedPrice,
      } = arg.data;

      if (arg.type === EDealRequestType.Purchase) {
        if (
          communicationType &&
          timeIntervalTypeCommunication &&
          timeIntervalTypeView &&
          viewRealtorParticipation
        ) {
          const purchaseReq: IDealPurchaseRequest = {
            marketType: arg.marketType,
            type: EDealRequestType.Purchase,
            communicationType: communicationType,
            communicationInterval: timeIntervalTypeCommunication,
            purchase: {
              readyForCredit: finance ?? undefined,
              purpose: aim ?? undefined,
              viewTime: timeIntervalTypeView,
              viewType: viewRealtorParticipation,
              desiredLocationIds: desiredLocationIds ?? undefined,
              rejectedLocationIds: rejectedLocationIds ?? undefined,
              facilityTypeIds: selectedFacilityTypeIds ?? undefined,
              desiredApartmentComment: freeComment ?? undefined,
            },
          };
          request = purchaseReq;
        } else if (facilityId) {
          const purchaseReq: IDealPurchaseRequestByFacilityId = {
            marketType: arg.marketType,
            type: EDealRequestType.Purchase,
            purchase: {
              facilityId,
            },
          };
          request = purchaseReq;
        }
      }

      if (arg.type === EDealRequestType.Sale) {
        if (
          communicationType &&
          timeIntervalTypeCommunication &&
          viewRealtorParticipation
        ) {
          const saleReq: IDealSaleRequest = {
            marketType: arg.marketType,
            type: EDealRequestType.Sale,
            communicationType: communicationType,
            communicationInterval: timeIntervalTypeCommunication,
            sale: {
              externalFacility: {
                streetId: streetId ?? undefined,
                complexId: complexId ?? undefined,
                areaInSqM: areaInSqM ?? undefined,
                buildingId: buildingId ?? undefined,
                typeId:
                  facilityTypeId !== null
                    ? facilityTypeId
                    : selectedFacilityTypeOwnFacility !== null
                    ? selectedFacilityTypeOwnFacility
                    : undefined,
              },
              estimatedPrice: estimatedPrice ?? undefined,
              apartmentNumber: apartmentNumber
                ? String(apartmentNumber)
                : undefined,
              desiredPrice: wishPriceSell ?? undefined,
              demonstrationType: viewRealtorParticipation ?? undefined,
              // demonstrationTime:
              readinessForLowerSellPrice: offersForClient ?? undefined,
              specificsOfSale: getSpecificsOfSale(arg.data),
            },
          };
          request = saleReq;
        }
      }

      if (arg.type === EDealRequestType.Ransom) {
        if (areaInSqM && buildingId && apartmentNumber) {
          const ransomReq: IDealRansomRequest = {
            marketType: arg.marketType,
            type: EDealRequestType.Ransom,
            ransom: {
              externalFacility: {
                streetId: streetId ?? undefined,
                complexId: complexId ?? undefined,
                areaInSqM: areaInSqM,
                buildingId: buildingId,
                typeId: facilityTypeId ?? undefined,
              },
              estimatedPrice: estimatedPrice ?? undefined,
              apartmentNumber: String(apartmentNumber),
            },
          };
          request = ransomReq;
        }
      }

      if (arg.type === EDealRequestType.Estimate) {
        if (areaInSqM && buildingId) {
          const estimateReq: IDealEstimateRequest = {
            marketType: arg.marketType,
            type: EDealRequestType.Estimate,
            estimate: {
              externalFacility: {
                complexId: complexId ?? undefined,
                streetId: streetId ?? undefined,
                areaInSqM: areaInSqM,
                buildingId: buildingId,
                typeId: facilityTypeId ?? undefined,
              },
            },
          };
          request = estimateReq;
        }
      }

      if (arg.type === EDealRequestType.DealService) {
        if (
          buildingId &&
          areaInSqM &&
          sideDeal &&
          facilityTypeId &&
          isAllParticipantDealInCity !== null &&
          isAllParticipantDealInCity !== undefined
        ) {
          const dealServiceReq: IDealServiceRequest = {
            marketType: arg.marketType,
            type: EDealRequestType.DealService,
            dealService: {
              externalFacility: {
                streetId: streetId ?? undefined,
                complexId: complexId ?? undefined,
                buildingId: buildingId,
                areaInSqM: areaInSqM,
                typeId: facilityTypeId,
              },
              specificsOfDeal: getSpecificsOfSale(arg.data),
              participationDeal: sideDeal,
              isAllParticipationDealInCity: isAllParticipantDealInCity,
            },
          };
          request = dealServiceReq;
        }
      }

      if (request) {
        const data = await fetcher.swr(url, {
          method: "POST",
          data: request,
        });
        return data.result;
      } else {
        toast.error(ERROR_TEXT);
        throw new Error(ERROR_TEXT);
      }
    } catch (error: any) {
      toast.error(
        `Что-то не так. Проверьте заполненные данные. Причина: ${error}`
      );
      throw new Error(error);
    }
  }

  const { data, error, isMutating, trigger } = useSWRMutation(
    "/deal-request",
    dealRequest
  );

  return { data: data, error, isMutating, trigger };
};

export default useDealRequest;
